import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import { superAdminApi } from '../../services/api';
import toast from 'react-hot-toast';

const NAV = [
  {
    to: '/super-admin', label: 'Dashboard', end: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
        <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
      </svg>
    ),
  },
  {
    to: '/super-admin/restaurants', label: 'Restaurants',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    to: '/super-admin/settings', label: 'Settings',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
      </svg>
    ),
  },
];

const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);
const LogoutIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const MenuIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const EyeIcon = ({ open }) => open ? (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const getInitials = (name) => {
  if (!name) return 'SA';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const SuperAdminLayout = () => {
  const { superAdmin, logout } = useSuperAdminAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Password modal
  const [showPassModal, setShowPassModal] = useState(false);
  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurr, setShowCurr] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleResetOwnPassword = async () => {
    if (!passForm.current_password || !passForm.new_password) return toast.error('Sabhi fields bharo');
    if (passForm.new_password.length < 6) return toast.error('Naya password kam se kam 6 characters ka hona chahiye');
    if (passForm.new_password !== passForm.confirm_password) return toast.error('Naya password aur confirm password match nahi kar rahe');
    setResetting(true);
    try {
      await superAdminApi.resetSuperAdminPassword({
        current_password: passForm.current_password,
        new_password: passForm.new_password,
      });
      toast.success('Aapka password successfully change ho gaya!');
      setShowPassModal(false);
      setPassForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Password change failed');
    } finally {
      setResetting(false);
    }
  };

  const closePassModal = () => {
    setShowPassModal(false);
    setPassForm({ current_password: '', new_password: '', confirm_password: '' });
    setShowCurr(false);
    setShowNew(false);
  };

  const collapsed = !isMobile && !sidebarOpen;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .sa-root {
          display: flex;
          min-height: 100vh;
          background: #f4f6fb;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        /* ── Overlay ── */
        .sa-overlay {
          position: fixed; inset: 0;
          background: rgba(30, 41, 59, 0.32);
          backdrop-filter: blur(3px);
          z-index: 98;
          animation: saFade 0.2s ease;
        }

        /* ── Sidebar ── */
        .sa-sidebar {
          position: fixed;
          top: 0; left: 0; bottom: 0;
          width: 250px;
          background: #ffffff;
          border-right: 1px solid #e9ebf4;
          display: flex;
          flex-direction: column;
          z-index: 100;
          transition: width 0.22s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1);
          box-shadow: 2px 0 16px rgba(99,102,241,0.06);
          overflow-y: auto; overflow-x: hidden;
        }
        .sa-sidebar::-webkit-scrollbar { width: 3px; }
        .sa-sidebar::-webkit-scrollbar-thumb { background: #e0e3ff; border-radius: 2px; }
        .sa-sidebar.sa-collapsed { width: 72px; }
        .sa-sidebar.sa-mobile-hidden { transform: translateX(-100%); }

        /* Brand */
        .sa-brand {
          display: flex; align-items: center; gap: 11px;
          padding: 18px 15px;
          border-bottom: 1px solid #f0f2fb;
          flex-shrink: 0;
        }
        .sa-brand-logo {
          width: 40px; height: 40px; border-radius: 11px;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 4px 14px rgba(99,102,241,0.3);
        }
        .sa-brand-texts { overflow: hidden; }
        .sa-brand-name {
          font-size: 14px; font-weight: 700; color: #1e293b;
          white-space: nowrap; line-height: 1.3;
        }
        .sa-brand-sub {
          font-size: 11px; font-weight: 500; color: #6366f1;
          white-space: nowrap; letter-spacing: 0.3px;
        }
        .sa-collapsed .sa-brand-texts { display: none; }

        /* Section label */
        .sa-sec-label {
          font-size: 10.5px; font-weight: 700; color: #94a3b8;
          letter-spacing: 0.9px; text-transform: uppercase;
          padding: 14px 15px 4px;
          white-space: nowrap; overflow: hidden;
          transition: opacity 0.18s;
        }
        .sa-collapsed .sa-sec-label { opacity: 0; }

        /* Nav */
        .sa-nav { padding: 4px 10px; display: flex; flex-direction: column; gap: 2px; }

        .sa-link {
          display: flex; align-items: center; gap: 11px;
          padding: 10px 10px;
          border-radius: 9px;
          text-decoration: none;
          font-size: 13.5px; font-weight: 500;
          color: #64748b;
          transition: all 0.13s ease;
          white-space: nowrap; overflow: hidden;
          position: relative;
        }
        .sa-link:hover { background: #f0f2ff; color: #4f46e5; }
        .sa-link.active { background: #eef0ff; color: #4338ca; font-weight: 600; }
        .sa-link-ico {
          flex-shrink: 0; color: #94a3b8;
          display: flex; align-items: center;
          transition: color 0.13s;
        }
        .sa-link:hover .sa-link-ico, .sa-link.active .sa-link-ico { color: #4f46e5; }
        .sa-link-txt {
          overflow: hidden;
          transition: opacity 0.18s, max-width 0.22s;
          max-width: 160px;
        }
        .sa-collapsed .sa-link-txt { opacity: 0; max-width: 0; }
        .sa-link-pip {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          width: 6px; height: 6px; border-radius: 50%;
          background: #6366f1;
          transition: opacity 0.15s;
        }
        .sa-collapsed .sa-link-pip { opacity: 0; }

        /* Spacer + divider */
        .sa-grow { flex: 1; }
        .sa-hr { height: 1px; background: #f0f2fb; margin: 4px 10px; }

        /* Sidebar footer */
        .sa-sfooter { padding: 10px 10px 14px; flex-shrink: 0; }

        .sa-user-tile {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px;
          background: #f8f9ff;
          border: 1px solid #eaecff;
          border-radius: 10px;
          margin-bottom: 8px;
          overflow: hidden;
        }
        .sa-avatar {
          width: 34px; height: 34px; border-radius: 9px;
          background: linear-gradient(135deg, #6366f1, #4338ca);
          color: #fff; font-size: 13px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .sa-uinfo { overflow: hidden; min-width: 0; }
        .sa-uname {
          font-size: 13px; font-weight: 600; color: #1e293b;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sa-uemail {
          font-size: 11px; color: #6366f1;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .sa-collapsed .sa-uinfo { display: none; }
        .sa-collapsed .sa-user-tile { justify-content: center; }

        .sa-fbtn {
          display: flex; align-items: center; gap: 9px;
          width: 100%; padding: 9px 10px;
          border-radius: 8px; border: none;
          font-size: 13px; font-weight: 500;
          font-family: 'Plus Jakarta Sans', sans-serif;
          cursor: pointer;
          transition: background 0.13s;
          background: transparent;
          white-space: nowrap; overflow: hidden;
        }
        .sa-fbtn-ico { flex-shrink: 0; display: flex; align-items: center; }
        .sa-fbtn-txt {
          overflow: hidden;
          transition: opacity 0.18s, max-width 0.22s;
          max-width: 160px;
        }
        .sa-collapsed .sa-fbtn-txt { opacity: 0; max-width: 0; }
        .sa-fbtn-reset { color: #4f46e5; }
        .sa-fbtn-reset:hover { background: #eef0ff; }
        .sa-fbtn-logout { color: #ef4444; }
        .sa-fbtn-logout:hover { background: #fff5f5; }

        /* ── Main ── */
        .sa-main {
          flex: 1; display: flex; flex-direction: column;
          min-height: 100vh; min-width: 0;
          margin-left: 250px;
          transition: margin-left 0.22s cubic-bezier(.4,0,.2,1);
          background: #f4f6fb;
        }
        .sa-main.sa-collapsed { margin-left: 72px; }
        .sa-main.sa-mobile { margin-left: 0; }

        /* Topbar */
        .sa-topbar {
          display: flex; align-items: center; gap: 12px;
          padding: 11px 24px;
          background: #fff;
          border-bottom: 1px solid #e9ebf4;
          position: sticky; top: 0; z-index: 50;
          box-shadow: 0 1px 6px rgba(99,102,241,0.06);
        }
        .sa-toggle {
          width: 36px; height: 36px; border-radius: 9px;
          background: #f4f6fb; border: 1px solid #e9ebf4;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #475569;
          transition: all 0.13s;
          flex-shrink: 0;
        }
        .sa-toggle:hover { background: #eef0ff; color: #4f46e5; border-color: #c7d2fe; }

        .sa-topbar-chip {
          display: flex; align-items: center; gap: 7px;
          padding: 5px 13px;
          background: #f8f9ff;
          border: 1px solid #e0e4ff;
          border-radius: 20px;
          font-size: 12.5px; font-weight: 500; color: #4f46e5;
        }
        .sa-pulse-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #22c55e;
          animation: saPulse 2.5s infinite;
        }

        .sa-topbar-end { display: flex; align-items: center; gap: 8px; margin-left: auto; }
        .sa-topbar-user {
          display: flex; align-items: center; gap: 8px;
          padding: 4px 10px 4px 5px;
          background: #f8f9ff;
          border: 1px solid #e0e4ff;
          border-radius: 22px;
        }
        .sa-topbar-av {
          width: 28px; height: 28px; border-radius: 7px;
          background: linear-gradient(135deg, #6366f1, #4338ca);
          color: #fff; font-size: 11px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
        }
        .sa-topbar-nm { font-size: 12.5px; font-weight: 600; color: #1e293b; }

        @media (max-width: 480px) {
          .sa-topbar { padding: 11px 14px; }
          .sa-topbar-chip { display: none; }
          .sa-topbar-nm { display: none; }
          .sa-topbar-user { padding: 4px 6px; }
        }

        /* Content */
        .sa-content { flex: 1; padding: 24px; }
        @media (max-width: 640px) { .sa-content { padding: 16px; } }

        /* ── Modal ── */
        .sa-modal-bg {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.42);
          backdrop-filter: blur(5px);
          z-index: 200;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
          animation: saFade 0.18s ease;
        }
        .sa-modal {
          background: #fff;
          border-radius: 18px;
          width: 100%; max-width: 400px;
          box-shadow: 0 24px 64px rgba(15,23,42,0.16), 0 0 0 1px rgba(99,102,241,0.1);
          overflow: hidden;
          animation: saUp 0.22s ease;
        }
        .sa-mhead {
          padding: 20px 22px 16px;
          border-bottom: 1px solid #f0f2fb;
          display: flex; align-items: center; gap: 12px;
        }
        .sa-mhead-ico {
          width: 38px; height: 38px; border-radius: 10px;
          background: #eef0ff;
          display: flex; align-items: center; justify-content: center;
          color: #4f46e5; flex-shrink: 0;
        }
        .sa-mhead-info { flex: 1; }
        .sa-mhead-title { font-size: 15px; font-weight: 700; color: #1e293b; }
        .sa-mhead-sub { font-size: 12px; color: #94a3b8; margin-top: 1px; }
        .sa-mclose {
          width: 30px; height: 30px; border-radius: 8px;
          background: #fff5f5; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: #ef4444; font-size: 13px; font-weight: 700;
          transition: background 0.13s;
        }
        .sa-mclose:hover { background: #fee2e2; }

        .sa-mbody { padding: 20px 22px 22px; display: flex; flex-direction: column; gap: 14px; }

        .sa-mfield label {
          display: block; font-size: 11.5px; font-weight: 600;
          color: #475569; margin-bottom: 5px; letter-spacing: 0.3px;
        }
        .sa-mfield-rel { position: relative; }
        .sa-minput {
          width: 100%;
          padding: 10px 38px 10px 13px;
          border: 1.5px solid #e2e8f0;
          border-radius: 9px;
          font-size: 13.5px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: #1e293b;
          background: #fafbff;
          outline: none;
          transition: border-color 0.13s, box-shadow 0.13s;
        }
        .sa-minput:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.11); }
        .sa-minput::placeholder { color: #b0bad3; }
        .sa-minput.plain { padding-right: 13px; }
        .sa-meye {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #94a3b8; display: flex; align-items: center;
          transition: color 0.13s; padding: 2px;
        }
        .sa-meye:hover { color: #4f46e5; }

        .sa-msubmit {
          width: 100%; padding: 11px;
          background: linear-gradient(135deg, #6366f1, #4338ca);
          color: #fff; border: none; border-radius: 9px;
          font-size: 13.5px; font-weight: 600;
          font-family: 'Plus Jakarta Sans', sans-serif;
          cursor: pointer;
          transition: opacity 0.18s, transform 0.1s;
          box-shadow: 0 4px 14px rgba(99,102,241,0.26);
          margin-top: 2px;
        }
        .sa-msubmit:hover:not(:disabled) { opacity: 0.91; transform: translateY(-1px); }
        .sa-msubmit:disabled { opacity: 0.42; cursor: not-allowed; transform: none; }

        @keyframes saFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes saUp {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes saPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
          60%       { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
        }
      `}</style>

      <div className="sa-root">

        {/* Mobile overlay */}
        {isMobile && mobileOpen && (
          <div className="sa-overlay" onClick={() => setMobileOpen(false)} />
        )}

        {/* ── Password Modal ── */}
        {showPassModal && (
          <div className="sa-modal-bg" onClick={e => e.target === e.currentTarget && closePassModal()}>
            <div className="sa-modal">
              <div className="sa-mhead">
                <div className="sa-mhead-ico"><LockIcon /></div>
                <div className="sa-mhead-info">
                  <div className="sa-mhead-title">Reset Password</div>
                  <div className="sa-mhead-sub">Update your account password</div>
                </div>
                <button className="sa-mclose" onClick={closePassModal}>✕</button>
              </div>
              <div className="sa-mbody">
                <div className="sa-mfield">
                  <label>Current Password</label>
                  <div className="sa-mfield-rel">
                    <input
                      type={showCurr ? 'text' : 'password'}
                      className="sa-minput"
                      placeholder="Enter current password"
                      value={passForm.current_password}
                      onChange={e => setPassForm(p => ({ ...p, current_password: e.target.value }))}
                    />
                    <button className="sa-meye" onClick={() => setShowCurr(p => !p)}>
                      <EyeIcon open={showCurr} />
                    </button>
                  </div>
                </div>
                <div className="sa-mfield">
                  <label>New Password</label>
                  <div className="sa-mfield-rel">
                    <input
                      type={showNew ? 'text' : 'password'}
                      className="sa-minput"
                      placeholder="Minimum 6 characters"
                      value={passForm.new_password}
                      onChange={e => setPassForm(p => ({ ...p, new_password: e.target.value }))}
                    />
                    <button className="sa-meye" onClick={() => setShowNew(p => !p)}>
                      <EyeIcon open={showNew} />
                    </button>
                  </div>
                </div>
                <div className="sa-mfield">
                  <label>Confirm New Password</label>
                  <div className="sa-mfield-rel">
                    <input
                      type="password"
                      className="sa-minput plain"
                      placeholder="Re-enter new password"
                      value={passForm.confirm_password}
                      onChange={e => setPassForm(p => ({ ...p, confirm_password: e.target.value }))}
                    />
                  </div>
                </div>
                <button className="sa-msubmit" onClick={handleResetOwnPassword} disabled={resetting}>
                  {resetting ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Sidebar ── */}
        <aside className={[
          'sa-sidebar',
          collapsed ? 'sa-collapsed' : '',
          isMobile ? (mobileOpen ? '' : 'sa-mobile-hidden') : '',
        ].filter(Boolean).join(' ')}>

          <div className="sa-brand">
            <div className="sa-brand-logo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <div className="sa-brand-texts">
              <div className="sa-brand-name">Super Admin</div>
              <div className="sa-brand-sub">Platform Control</div>
            </div>
          </div>

          <div className="sa-sec-label">Navigation</div>

          <nav className="sa-nav">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `sa-link${isActive ? ' active' : ''}`}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="sa-link-ico">{item.icon}</span>
                <span className="sa-link-txt">{item.label}</span>
                {/* active dot */}
                <span className="sa-link-pip" style={{ opacity: undefined }} />
              </NavLink>
            ))}
          </nav>

          <div className="sa-grow" />
          <div className="sa-hr" />

          <div className="sa-sfooter">
            <div className="sa-user-tile">
              <div className="sa-avatar">{getInitials(superAdmin?.name)}</div>
              <div className="sa-uinfo">
                <div className="sa-uname">{superAdmin?.name || 'Super Admin'}</div>
                <div className="sa-uemail">{superAdmin?.email || ''}</div>
              </div>
            </div>
            <button className="sa-fbtn sa-fbtn-reset" onClick={() => setShowPassModal(true)}>
              <span className="sa-fbtn-ico"><LockIcon /></span>
              <span className="sa-fbtn-txt">Reset Password</span>
            </button>
            <button className="sa-fbtn sa-fbtn-logout" onClick={logout}>
              <span className="sa-fbtn-ico"><LogoutIcon /></span>
              <span className="sa-fbtn-txt">Logout</span>
            </button>
          </div>
        </aside>

        {/* ── Main ── */}
        <main className={[
          'sa-main',
          collapsed ? 'sa-collapsed' : '',
          isMobile ? 'sa-mobile' : '',
        ].filter(Boolean).join(' ')}>

          <div className="sa-topbar">
            <button
              className="sa-toggle"
              onClick={() => isMobile ? setMobileOpen(p => !p) : setSidebarOpen(p => !p)}
            >
              <MenuIcon />
            </button>

            <div className="sa-topbar-chip">
              <div className="sa-pulse-dot" />
              Cafe Platform
            </div>

            <div className="sa-topbar-end">
              <div className="sa-topbar-user">
                <div className="sa-topbar-av">{getInitials(superAdmin?.name)}</div>
                <span className="sa-topbar-nm">{superAdmin?.name || 'Super Admin'}</span>
              </div>
            </div>
          </div>

          <div className="sa-content">
            <Outlet />
          </div>
        </main>

      </div>
    </>
  );
};

export default SuperAdminLayout;
